import { useState, useEffect } from 'react';
import { noop } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { waitForSessionStorageValue } from '../../utils/waitForSessionStorageValue';
import { UserRole } from '../../types';
import { getUserContext } from './getUserContext';

const LOADING = 'LOADING';
const SESSION_STORAGE_KEY = 'help_menu_package_userRole';
const safeSessionStorage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;

export const useUserRole = ({
    source,
    initialValue,
    enabled = true,
    onError = noop,
}: {
    source?: string;
    initialValue?: UserRole;
    enabled?: boolean;
    onError?: (error: unknown) => void;
}) => {
    const { userId } = getContext();
    const storageKey = `${SESSION_STORAGE_KEY}:${userId}`;
    const [userRole, setUserRole] = useState<UserRole | undefined>(() => {
        if (initialValue) {
            return initialValue;
        }

        const storedUserRole = safeSessionStorage?.getItem(storageKey);

        if (!storedUserRole || storedUserRole === LOADING) {
            return undefined;
        }

        return storedUserRole as UserRole;
    });
    const [isLoading, setIsLoading] = useState(false);

    const fetchUserRole = async (): Promise<UserRole | undefined> => {
        if (userRole) {
            return userRole;
        }

        const storedUserRole = safeSessionStorage?.getItem(storageKey);
        if (storedUserRole === LOADING) {
            try {
                const resolvedUserRole = await waitForSessionStorageValue(storageKey, (value) => value !== LOADING);

                setUserRole(resolvedUserRole as UserRole);
                return resolvedUserRole as UserRole;
            } catch (error) {
                logger.warn(error as Error);
                onError(error);
            }
        }

        setIsLoading(true);
        safeSessionStorage?.setItem(storageKey, LOADING);
        try {
            const { role } = await getUserContext({ source });
            setUserRole(role);
            safeSessionStorage?.setItem(storageKey, role);

            return role;
        } catch (error) {
            setUserRole(undefined);
            safeSessionStorage?.removeItem(storageKey);
            onError(error);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        if (isLoading || !enabled || !!userRole) {
            return;
        }

        fetchUserRole();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, userRole, enabled, source, onError]);

    useEffect(() => {
        const handler = () => {
            if (!userId) {
                return;
            }

            safeSessionStorage?.removeItem(`${SESSION_STORAGE_KEY}:${userId}`);
        };

        addEventListener('beforeunload', handler);
    }, [userId]);

    return {
        isLoading,
        userRole,
        fetchUserRole,
    };
};
